import React, { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Save, Loader2 } from 'lucide-react'
import { showSaveSuccess, showSaveError, showValidationError, showLoading, closeSwal, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, SearchableSelectOption } from '@/components/ui/searchable-select'
import { InvoiceMultiSelect } from '@/components/ui/invoice-multiselect'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { receiptsService } from '@/services/receiptsService'
import { financeService } from '@/services/financeService'
import {
  ReceiptVm,
  FinanceCustomer,
  ReceiptAccount,
  CustomerInvoice,
  CustomerAdvanceForReceipt,
} from '@/api/types'
import { calendarToday, calendarFromApi, calendarToApi } from '@/lib/calendarDate'

export const ReceiptsDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const isEditMode = id !== 'new' && id !== undefined
  const isViewMode = isEditMode
  const branchId = user?.selectedBranch?.branchId

  const [receipt, setReceipt] = useState<ReceiptVm>({
    receiptId: 0,
    receiptDate: calendarToday(),
    customerId: 0,
    receiptNo: '',
    refNo: '',
    paymentType: '',
    amount: 0,
    recipientAccount: 0,
    destinationAccount: 0,
    note: '',
    advanceId: 0,
    ipAddress: '',
    branchId: branchId || 0,
    invoiceIds: [], // Now array of ReceiptInvoiceLine objects
  })

  // Additional form fields for display
  const [selectedAdvanceAmount, setSelectedAdvanceAmount] = useState<number>(0)
  const [, setPreviousReceiptAmount] = useState<number>(0)
  const [, setAdvanceLinesAmount] = useState<number>(0)
  
  // Invoice table data - each row represents an invoice with its payment details
  interface InvoiceTableRow {
    invoiceId: number
    invoiceNo: string
    payStatus: string
    totalAmount: number
    paidAmount: number
    remainingAmount: number
    originalRemainingAmount: number
    amount: number
    lineId?: number
  }
  const [invoiceTableData, setInvoiceTableData] = useState<InvoiceTableRow[]>([])
  const skipAllocate = useRef(isEditMode)

  const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100

  const pickNum = (obj: any, ...keys: string[]) => {
    if (!obj) return 0
    for (const key of keys) {
      const raw = obj[key]
      if (raw !== undefined && raw !== null && raw !== '') {
        const n = Number(raw)
        if (!Number.isNaN(n)) return n
      }
    }
    return 0
  }

  const pickStr = (obj: any, ...keys: string[]) => {
    if (!obj) return ''
    for (const key of keys) {
      const raw = obj[key]
      if (raw !== undefined && raw !== null && String(raw) !== '') return String(raw)
    }
    return ''
  }

  const getAdvanceId = (adv: CustomerAdvanceForReceipt | undefined) =>
    Number(adv?.id ?? (adv as any)?.Id ?? adv?.advanceId ?? (adv as any)?.AdvanceId ?? 0)

  const getAdvanceAvailable = (adv: CustomerAdvanceForReceipt | undefined, extraUsedOnThisReceipt = 0) => {
    if (!adv) return 0
    const total = Number(adv.amount ?? (adv as any).Amount ?? 0)
    const used = Number(adv.amountUsed ?? (adv as any).AmountUsed ?? 0)
    return Math.max(0, round2(total - used + extraUsedOnThisReceipt))
  }

  const cashAfterAdvance = (lineTotal: number, advanceAvailable: number) =>
    Math.max(0, round2(lineTotal - Math.max(0, advanceAvailable)))

  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [accounts, setAccounts] = useState<ReceiptAccount[]>([])
  const [invoices, setInvoices] = useState<CustomerInvoice[]>([])
  const [advances, setAdvances] = useState<CustomerAdvanceForReceipt[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<FinanceCustomer | null>(null)
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [allocationLocked, setAllocationLocked] = useState(false)

  // Payment type options
  const paymentTypeOptions = [
    { value: 'Bank', label: t('receipts.bank') },
    { value: 'Cash', label: t('receipts.cash') },
  ]

  // Load customers and accounts
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        setLoading(true)
        const [customersData, accountsData] = await Promise.all([
          financeService.getAllCustomers(branchId),
          receiptsService.getAllAccounts(branchId),
        ])

        setCustomers(customersData || [])
        setAccounts(accountsData || [])
      } catch (error: any) {
        console.error('Failed to load lookups', error)
        await showError('Error', error.response?.data?.message || t('receipts.failedToLoadData'))
      } finally {
        setLoading(false)
      }
    }

    loadLookups()
  }, [branchId])

  // Load invoices when customer is selected
  useEffect(() => {
    if (!selectedCustomer?.customerId) {
      setInvoices([])
      setSelectedInvoiceIds([])
      return
    }

    const loadInvoices = async () => {
      try {
        const invoicesData = await receiptsService.getCustomerInvoices(selectedCustomer.customerId)
        setInvoices(invoicesData || [])
      } catch (error: any) {
        console.error('Failed to load invoices', error)
        await showError('Error', error.response?.data?.message || t('receipts.failedToLoadInvoices'))
      }
    }

    loadInvoices()
  }, [selectedCustomer])

  useEffect(() => {
    if (skipAllocate.current) return

    if (selectedInvoiceIds.length === 0) {
      setInvoiceTableData([])
      return
    }

    setInvoiceTableData((prev) =>
      selectedInvoiceIds.map((idStr) => {
        const inv = invoices.find((i) => String(pickNum(i, 'invoiceId', 'InvoiceId')) === idStr)
        const existing = prev.find((r) => String(r.invoiceId) === idStr)
        const remainingFromInv = pickNum(inv, 'remainingAmount', 'RemainingAmount')
        const previouslyPaid = pickNum(inv, 'paidAmount', 'PaidAmount') || existing?.paidAmount || 0
        const totalAmount = pickNum(inv, 'total', 'Total') || existing?.totalAmount || 0
        const originalRemaining =
          existing && existing.originalRemainingAmount > 0 ? existing.originalRemainingAmount : remainingFromInv
        const typed = Math.min(existing?.amount || 0, Math.max(0, originalRemaining))
        return {
          invoiceId: pickNum(inv, 'invoiceId', 'InvoiceId') || existing?.invoiceId || Number(idStr),
          invoiceNo: pickStr(inv, 'invoiceNo', 'InvoiceNo') || existing?.invoiceNo || '',
          payStatus:
            originalRemaining === 0
              ? 'Paid'
              : previouslyPaid === 0
                ? 'Not Paid'
                : (pickStr(inv, 'payStatus', 'PayStatus') || existing?.payStatus || 'Partially Paid'),
          totalAmount,
          paidAmount: previouslyPaid,
          remainingAmount: round2(originalRemaining - typed),
          originalRemainingAmount: originalRemaining,
          amount: typed,
          lineId: existing?.lineId || 0,
        }
      })
    )
  }, [selectedInvoiceIds, invoices])

  useEffect(() => {
    if (isViewMode) return
    const totalLines = round2(invoiceTableData.reduce((sum, row) => sum + (row.amount || 0), 0))
    const cash = cashAfterAdvance(totalLines, selectedAdvanceAmount)
    setReceipt((prev) => (Math.abs((prev.amount || 0) - cash) < 0.001 ? prev : { ...prev, amount: cash }))
  }, [invoiceTableData, selectedAdvanceAmount, isViewMode])

  // Load advances when customer is selected
  useEffect(() => {
    if (!selectedCustomer?.customerId) {
      setAdvances([])
      return
    }

    const loadAdvances = async () => {
      try {
        // For new receipt, receiptId is 0. For edit mode, use actual receiptId
        const receiptId = isEditMode && receipt.receiptId ? receipt.receiptId : 0
        const advancesData = await receiptsService.getCustomerAdvances(selectedCustomer.customerId, receiptId)
        setAdvances(advancesData || [])
      } catch (error: any) {
        console.error('Failed to load advances', error)
        await showError('Error', error.response?.data?.message || t('receipts.failedToLoadAdvances'))
      }
    }

    loadAdvances()
  }, [selectedCustomer, isEditMode, receipt.receiptId])

  // Load receipt data in edit mode
  useEffect(() => {
    if (!isEditMode || !branchId || !id) return
    if (customers.length === 0) {
      // Wait for customers to load
      return
    }

    const loadReceipt = async () => {
      try {
        setLoading(true)
        skipAllocate.current = true
        const receiptId = Number(id)
        const receiptData = await receiptsService.getReceiptById(branchId, receiptId)

        // Store previous receipt amount for calculations
        const previousAmount = receiptData.amount || 0
        setPreviousReceiptAmount(previousAmount)

        // Load advance lines for this receipt
        let advanceLinesTotal = 0
        let advanceLines: any[] = []
        try {
          advanceLines = await receiptsService.getCustomerAdvanceLinesByReceiptId(receiptId)
          advanceLinesTotal = advanceLines.reduce((sum: number, line: any) => {
            return sum + Number(line.amount ?? line.Amount ?? 0)
          }, 0)
          setAdvanceLinesAmount(advanceLinesTotal)
        } catch (error) {
          console.warn('Failed to load advance lines', error)
          setAdvanceLinesAmount(0)
        }

        // Find customer
        const customer = customers.find(c => c.customerId === receiptData.customerId)
        if (customer) {
          setSelectedCustomer(customer)
        }

        // Load receipt lines to get all invoice IDs and amounts
        let receiptLines: any[] = []
        try {
          receiptLines = await receiptsService.getReceiptLinesByReceiptId(receiptId)
          console.log('Receipt lines loaded:', receiptLines)
        } catch (error) {
          console.warn('Failed to load receipt lines', error)
        }

        setAllocationLocked(
          receiptLines.some((line: any) => Number(line.returnedAmount ?? line.ReturnedAmount ?? 0) > 0)
        )

        // Load invoices for customer
        if (receiptData.customerId) {
          const invoicesData = await receiptsService.getCustomerInvoices(receiptData.customerId)
          setInvoices(invoicesData || [])
          
          // Extract invoice IDs from receipt lines
          const invoiceIdsFromLines = receiptLines
            .map((line: any) => pickNum(line, 'invoiceId', 'InvoiceId'))
            .filter((invoiceId: number) => invoiceId > 0)
          
          if (invoiceIdsFromLines.length > 0) {
            const selectedInvoicesList = (invoicesData || []).filter((inv) =>
              invoiceIdsFromLines.includes(pickNum(inv, 'invoiceId', 'InvoiceId'))
            )
            setSelectedInvoiceIds(invoiceIdsFromLines.map((invoiceId: number) => String(invoiceId)))

            const tableData: InvoiceTableRow[] = invoiceIdsFromLines.map((invoiceId: number) => {
              const inv =
                selectedInvoicesList.find((i) => pickNum(i, 'invoiceId', 'InvoiceId') === invoiceId) ||
                ({ invoiceId, invoiceNo: `Invoice ${invoiceId}` } as CustomerInvoice)
              const receiptLine = receiptLines.find((line: any) => pickNum(line, 'invoiceId', 'InvoiceId') === invoiceId)
              const receiptLineAmount = pickNum(receiptLine, 'paidAmount', 'PaidAmount', 'amount')
              const lineId = pickNum(receiptLine, 'lineId', 'LineId')
              const currentPaid = pickNum(inv, 'paidAmount', 'PaidAmount')
              const currentRemaining = pickNum(inv, 'remainingAmount', 'RemainingAmount')
              const recalculatedPaidAmount = Math.max(0, currentPaid - receiptLineAmount)
              const recalculatedRemainingAmount = currentRemaining + receiptLineAmount

              return {
                invoiceId,
                invoiceNo: pickStr(inv, 'invoiceNo', 'InvoiceNo') || `Invoice ${invoiceId}`,
                payStatus: recalculatedRemainingAmount === 0 ? 'Paid' : (recalculatedPaidAmount === 0 ? 'Not Paid' : 'Partially Paid'),
                totalAmount: pickNum(inv, 'total', 'Total'),
                paidAmount: recalculatedPaidAmount,
                remainingAmount: recalculatedRemainingAmount,
                originalRemainingAmount: recalculatedRemainingAmount,
                amount: receiptLineAmount,
                lineId,
              }
            })
            setInvoiceTableData(tableData)
          } else {
            // Fallback to single invoice if no receipt lines found (backward compatibility)
            const invoice = invoicesData.find(inv => inv.invoiceId === receiptData.invoiceId)
            if (invoice) {
              setSelectedInvoiceIds([String(invoice.invoiceId)])
              
              // Find advance lines for this specific invoice
              const recalculatedPaidAmount = (invoice.paidAmount || 0) - previousAmount
              const recalculatedRemainingAmount = (invoice.remainingAmount || 0) + previousAmount
              
              // Populate table data
              const tableData: InvoiceTableRow[] = [{
                invoiceId: invoice.invoiceId,
                invoiceNo: invoice.invoiceNo || '',
                payStatus: recalculatedRemainingAmount === 0 ? 'Paid' : (recalculatedPaidAmount === 0 ? 'Not Paid' : 'Partially Paid'),
                totalAmount: Number(invoice.total || 0),
                paidAmount: recalculatedPaidAmount > 0 ? recalculatedPaidAmount : 0,
                remainingAmount: recalculatedRemainingAmount,
                originalRemainingAmount: recalculatedRemainingAmount,
                amount: previousAmount,
              }]
              setInvoiceTableData(tableData)
            }
          }
        }

        // Load advances for customer (pass receiptId for edit mode)
        if (receiptData.customerId) {
          try {
            const advancesData = await receiptsService.getCustomerAdvances(receiptData.customerId, receiptId)
            setAdvances(advancesData || [])
            
            // Find selected advance
            if (receiptData.advanceId) {
              const advance = advancesData.find((adv) => getAdvanceId(adv) === receiptData.advanceId)
              if (advance) {
                setSelectedAdvanceAmount(getAdvanceAvailable(advance, advanceLinesTotal))
              }
            }
          } catch (error) {
            console.warn('Failed to load customer advances', error)
            setAdvances([])
          }
        }

        setReceipt({
          receiptId: receiptData.receiptId || 0,
          receiptDate: calendarFromApi(receiptData.receiptDate) || calendarToday(),
          customerId: receiptData.customerId || 0,
          receiptNo: receiptData.receiptNo || '',
          refNo: receiptData.refNo || '',
          paymentType: receiptData.paymentType || '',
          amount: receiptData.amount || 0,
          recipientAccount: receiptData.recipientAccount || 0,
          destinationAccount: receiptData.destinationAccount || 0,
          note: receiptData.note || '',
          advanceId: receiptData.advanceId || 0,
          ipAddress: receiptData.ipAddress || '',
          branchId: receiptData.branchId || branchId || 0,
          invoiceIds: [], // Will be populated from invoiceTableData
        })
      } catch (error: any) {
        console.error('Error loading receipt', error)
        await showError('Error', error.message || t('receipts.failedToLoadReceipt'))
      } finally {
        setLoading(false)
      }
    }

    loadReceipt()
  }, [isEditMode, branchId, id, customers.length])

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.customerId === Number(customerId))
    if (customer) {
      skipAllocate.current = false
      setSelectedCustomer(customer)
      setReceipt(prev => ({
        ...prev,
        customerId: customer.customerId || 0,
        invoiceIds: [],
        advanceId: 0,
      }))
      setSelectedInvoiceIds([])
      setInvoiceTableData([])
      setSelectedAdvanceAmount(0)
    }
  }

  const handleInvoiceChange = (invoiceIds: string[]) => {
    skipAllocate.current = false
    setSelectedInvoiceIds(invoiceIds)
  }

  const handleAdvanceChange = (advanceId: string) => {
    skipAllocate.current = false
    if (!advanceId || advanceId === '') {
      setSelectedAdvanceAmount(0)
      setReceipt(prev => ({
        ...prev,
        advanceId: 0,
      }))
      return
    }

    const advance = advances.find((adv) => String(getAdvanceId(adv)) === advanceId)
    if (advance) {
      setSelectedAdvanceAmount(getAdvanceAvailable(advance))
      setReceipt((prev) => ({
        ...prev,
        advanceId: getAdvanceId(advance),
      }))
    }
  }

  const handleLinePaidChange = (invoiceId: number, value: number) => {
    if (isViewMode) return
    setInvoiceTableData((prev) =>
      prev.map((row) => {
        if (row.invoiceId !== invoiceId) return row
        const cap = round2(Math.max(0, row.originalRemainingAmount || 0))
        const paid = round2(Math.min(cap, Math.max(0, Number.isFinite(value) ? value : 0)))
        return {
          ...row,
          amount: paid,
          remainingAmount: round2(cap - paid),
        }
      })
    )
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  const handleSave = async () => {
    if (isViewMode) return
    if (!branchId) {
      await showValidationError(t('receipts.pleaseSelectBranch'))
      return
    }

    if (selectedInvoiceIds.length === 0) {
      await showValidationError(t('receipts.pleaseSelectInvoice'))
      return
    }

    if (!receipt.recipientAccount || receipt.recipientAccount === 0) {
      await showValidationError(t('receipts.pleaseSelectRecipientAccount'))
      return
    }

    if (!receipt.paymentType) {
      await showValidationError(t('receipts.pleaseSelectPaymentType'))
      return
    }

    // Amount validation: If advance is selected, amount can be 0. Otherwise, amount must be > 0
    const hasAdvance = receipt.advanceId && receipt.advanceId > 0
    if (!hasAdvance && (!receipt.amount || receipt.amount <= 0)) {
      await showValidationError(t('receipts.pleaseEnterValidAmount'))
      return
    }

    if (!receipt.receiptDate) {
      await showValidationError(t('receipts.pleaseSelectReceiptDate'))
      return
    }

    const totalAllocated = round2(invoiceTableData.reduce((sum, row) => sum + (row.amount || 0), 0))
    const totalCap = round2(invoiceTableData.reduce((sum, row) => sum + (row.originalRemainingAmount || 0), 0))
    const expectedCash = cashAfterAdvance(totalAllocated, selectedAdvanceAmount)

    if (totalAllocated <= 0) {
      await showValidationError(t('receipts.pleaseEnterValidAmount'))
      return
    }
    if (totalAllocated > totalCap + 0.001) {
      await showValidationError(t('receipts.amountCannotExceed', { amount: totalCap.toFixed(2) }))
      return
    }
    if (Math.abs((receipt.amount || 0) - expectedCash) > 0.01) {
      setReceipt((prev) => ({ ...prev, amount: expectedCash }))
    }

    // Declare receiptToSave outside try block for error logging
    let receiptToSave: ReceiptVm | null = null

    try {
      setSaving(true)
      
      // Show loading
      showLoading('Saving Receipt...', 'Please wait while we process your request')

      // Get logged-in user's IP address (not server's IP)
      const ipAddress = await getUserIPAddress()

      const dateISO = calendarToApi(receipt.receiptDate)

      // Prepare invoiceIds array with new structure
      const invoiceIdsArray = invoiceTableData
        .filter(row => (row.amount || 0) > 0)
        .map(row => ({
          lineId: row.lineId || 0,
          invoiceId: row.invoiceId,
          amount: row.amount || 0,
          isDeleted: 'false',
        }))

      // Prepare payload matching exact API structure
      receiptToSave = {
        receiptId: isEditMode ? (receipt.receiptId || 0) : 0, // Always 0 for new receipts
        receiptDate: dateISO, // ISO format
        customerId: selectedCustomer?.customerId || 0,
        receiptNo: receipt.receiptNo || undefined, // Don't send empty string, let backend generate
        refNo: receipt.refNo || '',
        paymentType: receipt.paymentType,
        recipientAccount: receipt.recipientAccount,
        destinationAccount: receipt.destinationAccount || 0,
        note: receipt.note || '',
        advanceId: receipt.advanceId || 0,
        ipAddress: ipAddress, // Set logged-in user's IP address
        branchId: branchId || 0,
        invoiceIds: invoiceIdsArray, // New structure with lineId, invoiceId, amount, isDeleted
        amount: expectedCash,
      }

      // Enhanced console logging for better visibility
      console.group('🔵 RECEIPT SAVE PAYLOAD')
      console.log('Mode: ➕ INSERT')
      console.log('Endpoint:', 'POST /api/ApiReceipts/Insert')
      console.table(receiptToSave)
      console.log('Full Payload JSON:')
      console.log(JSON.stringify(receiptToSave, null, 2))
      console.log('Invoice Lines Array:', receiptToSave.invoiceIds)
      console.log('Total Amount:', receiptToSave.amount)
      console.log('Selected Customer ID:', selectedCustomer?.customerId)
      console.groupEnd()

      await receiptsService.insertReceipt(receiptToSave)

      // Close loading
      closeSwal()
      
      // Show success
      await showSaveSuccess(false, t('receipts.receiptCreatedSuccess'))

      navigate('/receipts')
    } catch (error: any) {
      // Enhanced error logging
      console.group('❌ RECEIPT SAVE ERROR')
      console.error('Error Type:', error.name)
      console.error('Error Message:', error.message)
      console.error('HTTP Status:', error.response?.status)
      console.error('Error Response Data:')
      console.error(error.response?.data)
      
      if (error.response?.data?.msg) {
        console.error('Backend Error Message:', error.response.data.msg)
      }
      
      if (error.response?.data?.errors) {
        console.error('Validation Errors:', error.response.data.errors)
      }
      
      if (receiptToSave) {
        console.error('Request Payload (that caused error):')
        console.error(JSON.stringify(receiptToSave, null, 2))
      } else {
        console.error('Request Payload: Could not reconstruct (error occurred before payload creation)')
      }
      console.groupEnd()
      
      // Close loading if still open
      closeSwal()
      
      // Show error
      await showSaveError(error, false)
    } finally {
      setSaving(false)
    }
  }

  const customerOptions: SearchableSelectOption[] = customers.map((cust) => ({
    value: String(cust.customerId || 0),
    label: cust.customerNameEn || cust.customerNameAr || `Customer ${cust.customerId}`,
  }))

  // Filter invoices based on mode
  // In add new mode: exclude invoices with remaining amount 0
  // In edit mode: only show already selected invoices (regardless of remaining amount)
  const filteredInvoices = isEditMode
    ? invoices.filter((inv) => selectedInvoiceIds.includes(String(pickNum(inv, 'invoiceId', 'InvoiceId'))))
    : invoices.filter((inv) => pickNum(inv, 'remainingAmount', 'RemainingAmount') > 0)

  const invoiceOptions: SearchableSelectOption[] = filteredInvoices.map((inv) => ({
    value: String(pickNum(inv, 'invoiceId', 'InvoiceId') || 0),
    label: pickStr(inv, 'invoiceNo', 'InvoiceNo'),
    disabled: isEditMode,
  }))

  const advanceOptions: SearchableSelectOption[] = advances
    .map((adv) => {
      const id = getAdvanceId(adv)
      const available = getAdvanceAvailable(adv)
      if (id <= 0 || available <= 0) return null
      return {
        value: String(id),
        label: `${adv.advNo || adv.customerAdvanceNo || (adv as any).CustomerAdvanceNo || `Advance ${id}`} - Remaining: ${available.toFixed(2)}`,
      }
    })
    .filter((opt): opt is SearchableSelectOption => Boolean(opt))

  const accountOptions: SearchableSelectOption[] = accounts.map((acc) => {
    const accountCode = acc.accountCode || ''
    const accountName = acc.accountNameEn || acc.accountName || acc.accountNameAr || `Account ${acc.id}`
    const label = accountCode ? `${accountCode} (${accountName})` : accountName
    return {
      value: String(acc.id || acc.accountId || 0),
      label: label,
    }
  })

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('receipts.title')} description={isViewMode ? t('receipts.viewReceipt') : t('receipts.createNew')} />
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('receipts.title')}
        description={isViewMode ? t('receipts.viewReceipt') : t('receipts.createNew')}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            {/* Customer and Invoice Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>
                  {t('receipts.customer')} <span className="text-destructive">*</span>
                </Label>
                <div className="max-w-[400px]">
                  <SearchableSelect
                    options={customerOptions}
                    value={selectedCustomer ? String(selectedCustomer.customerId) : ''}
                    onValueChange={handleCustomerChange}
                    placeholder={t('receipts.selectCustomer')}
                    searchPlaceholder={t('receipts.searchCustomers')}
                    emptyMessage={t('receipts.noCustomersFound')}
                    disabled={isViewMode || allocationLocked}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('receipts.amount')}</Label>
                <div className="max-w-[400px]">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={receipt.amount || 0}
                    readOnly
                    disabled
                    placeholder="0.00"
                    className="bg-muted cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('receipts.amountCalculated')}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('receipts.advance')}</Label>
                <div className="max-w-[400px]">
                  <SearchableSelect
                    options={advanceOptions}
                    value={receipt.advanceId ? String(receipt.advanceId) : ''}
                    onValueChange={handleAdvanceChange}
                    placeholder={t('receipts.selectAdvance')}
                    searchPlaceholder={t('receipts.searchAdvances')}
                    emptyMessage={t('receipts.noAdvancesFound')}
                    disabled={isViewMode || !selectedCustomer || allocationLocked}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>
                  {t('receipts.invoiceRefNo')} <span className="text-destructive">*</span>
                </Label>
                <div className="max-w-[400px]">
                  <InvoiceMultiSelect
                    options={invoiceOptions}
                    value={selectedInvoiceIds}
                    onValueChange={isEditMode ? undefined : handleInvoiceChange}
                    placeholder={t('receipts.selectInvoice')}
                    searchPlaceholder={t('receipts.searchInvoices')}
                    emptyMessage={t('receipts.noInvoicesFound')}
                    disabled={isViewMode || !selectedCustomer || allocationLocked}
                  />
                </div>
              </div>
            </div>

            {/* Invoice Details Table */}
            {invoiceTableData.length > 0 && (
              <div className="space-y-2">
                <Label>{t('receipts.invoiceDetails') || 'Invoice Details'}</Label>
                <Card>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('receipts.invoiceNo')}</TableHead>
                            <TableHead className="text-right">{t('receipts.invoiceTotal')}</TableHead>
                            <TableHead className="text-right">{t('receipts.previouslyPaid')}</TableHead>
                            <TableHead className="text-right">{t('receipts.paidAmount')}</TableHead>
                            <TableHead className="text-right">{t('receipts.remainingAmount')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoiceTableData.map((row) => {
                            const originalRemaining = row.originalRemainingAmount || row.remainingAmount
                            const currentRemaining = round2(originalRemaining - (row.amount || 0))

                            return (
                              <TableRow key={row.invoiceId}>
                                <TableCell className="font-medium">{row.invoiceNo}</TableCell>
                                <TableCell className="text-right">{Number(row.totalAmount || 0).toFixed(2)}</TableCell>
                                <TableCell className="text-right">{Number(row.paidAmount || 0).toFixed(2)}</TableCell>
                                <TableCell className="text-right">
                                  {isViewMode ? (
                                    Number(row.amount || 0).toFixed(2)
                                  ) : (
                                    <Input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={row.amount || ''}
                                      onChange={(e) => handleLinePaidChange(row.invoiceId, Number(e.target.value) || 0)}
                                      className="h-8 w-28 ml-auto text-right"
                                    />
                                  )}
                                </TableCell>
                                <TableCell className="text-right">{currentRemaining >= 0 ? currentRemaining.toFixed(2) : '0.00'}</TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Receipt Info Section */}
            <div className="space-y-4">
              <CardHeader className="px-0 pt-0">
                <CardTitle>{t('receipts.receiptInfo')}</CardTitle>
              </CardHeader>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isEditMode && (
                  <div className="space-y-2">
                    <Label>{t('receipts.receiptNo')}</Label>
                    <div className="max-w-[400px]">
                      <Input
                        value={receipt.receiptNo || ''}
                        onChange={(e) => setReceipt(prev => ({ ...prev, receiptNo: e.target.value }))}
                        placeholder={t('receipts.enterReceiptNumber')}
                        readOnly={true}
                        className="bg-muted cursor-not-allowed"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{t('receipts.receiptDate')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      type="date"
                      value={receipt.receiptDate}
                      onChange={(e) => setReceipt(prev => ({ ...prev, receiptDate: e.target.value }))}
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('receipts.paymentType')}</Label>
                  <div className="max-w-[400px]">
                    <Select
                      value={receipt.paymentType}
                      onValueChange={(value) => setReceipt(prev => ({ ...prev, paymentType: value }))}
                      disabled={isViewMode}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('receipts.selectPaymentType')} />
                      </SelectTrigger>
                      <SelectContent searchable={false}>
                        {paymentTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('receipts.recipientAccount')}</Label>
                  <div className="max-w-[400px]">
                    <SearchableSelect
                      options={accountOptions}
                      value={receipt.recipientAccount ? String(receipt.recipientAccount) : ''}
                      onValueChange={(value) => setReceipt(prev => ({ ...prev, recipientAccount: Number(value) }))}
                      placeholder={t('receipts.selectAccount')}
                      searchPlaceholder={t('receipts.searchAccounts')}
                      emptyMessage={t('receipts.noAccountsFound')}
                      disabled={isViewMode}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('receipts.refNo')}</Label>
                  <div className="max-w-[400px]">
                    <Input
                      value={receipt.refNo || ''}
                      onChange={(e) => setReceipt(prev => ({ ...prev, refNo: e.target.value }))}
                      placeholder={t('receipts.enterReferenceNumber')}
                      disabled={isViewMode}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Note Section */}
            <div className="space-y-2">
              <Label>{t('receipts.note')}</Label>
              <Textarea
                value={receipt.note || ''}
                onChange={(e) => setReceipt(prev => ({ ...prev, note: e.target.value }))}
                placeholder={t('receipts.enterNotes')}
                rows={4}
                disabled={isViewMode}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/receipts')}
          className="w-full sm:w-auto"
        >
          {t('common.cancel')}
        </Button>
        {!isViewMode && (
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full sm:w-auto min-w-[150px]"
        >
          {saving ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t('receipts.saving')}
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {t('receipts.saveReceipt')}
            </>
          )}
        </Button>
        )}
      </div>
    </div>
  )
}

